import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext, enviarPayloadFacturaElectronica, emitirEcfDgii, revertirNotaCreditoRechazada } from 'context';
import { FP, fpInputSx } from 'components/financepro';
import VistaNotaCredito from './VistaNotaCredito';

/**
 * Diálogo para emitir Nota de Crédito a partir de una factura.
 * Carga la factura completa, permite devolver por monto específico o por ítems (completo o parcial).
 * Guarda el NCF de la factura original y emite NCF para la nota de crédito.
 * El permiso de modificación se gestiona en la pantalla padre (Mantenimiento Nota de Crédito).
 */
const NotaCredito = ({ open, onClose, facturaOrigen, puedeModificar = true }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, direccion_empresa: direccionEmpresa, nombreNegocio, rnc_empresa: rncEmpresa, comprobanteelectronico } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const [motivo, setMotivo] = useState('');
  const [tipoDevolucion, setTipoDevolucion] = useState('monto');
  const [monto, setMonto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [cargandoFactura, setCargandoFactura] = useState(false);
  const [facturaCompleta, setFacturaCompleta] = useState(null);
  const [itemsConCantidad, setItemsConCantidad] = useState([]);
  const [vistaNcAbierta, setVistaNcAbierta] = useState(false);
  const [datosVistaNc, setDatosVistaNc] = useState(null);
  const [reglasImpuestos, setReglasImpuestos] = useState([]);
  const [totalesNotaCredito, setTotalesNotaCredito] = useState({
    impuestos: [],
    totalImpuestos: 0,
    totalDescuentos: 0,
    total: 0,
  });
  const [calculandoImpuestosNc, setCalculandoImpuestosNc] = useState(false);
  const [fiscalListo, setFiscalListo] = useState(true);

  
  const cargarFacturaCompleta = useCallback(async () => {
    if (!facturaOrigen || !selectedCompany?.db_empresa) return;
    setCargandoFactura(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerfactura', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          nfact: facturaOrigen.nfact,
          tipofactura: facturaOrigen.tipofactura,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const factura = data.factura || {};
        setFacturaCompleta(factura);
        const items = factura.items || [];
        setItemsConCantidad(items.map((it) => ({ ...it, cantidadDevolver: it.cantidad })));
        const disponible = factura.monto_disponible_a_devolver ?? (parseFloat(factura.total) || 0);
        setMonto(String(disponible));
      } else {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'Error al cargar la factura');
      }
    } catch {
      showError('Error de conexión al cargar la factura');
    } finally {
      setCargandoFactura(false);
    }
  }, [facturaOrigen, selectedCompany, usuarioLog, claveLog, servidor, showError]);

  useEffect(() => {
    if (!open) {
      setVistaNcAbierta(false);
      setDatosVistaNc(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && facturaOrigen?.nfact != null && facturaOrigen?.tipofactura) {
      setMotivo('');
      setTipoDevolucion('monto');
      setFacturaCompleta(null);
      setItemsConCantidad([]);
      setMonto(String(parseFloat(facturaOrigen.total) || '0'));
      cargarFacturaCompleta();
    }
  }, [open, facturaOrigen?.nfact, facturaOrigen?.tipofactura, cargarFacturaCompleta]);

  useEffect(() => {
    if (!open || !selectedCompany?.db_empresa) return;
    let cancelled = false;
    fetch(servidor + '/api/facturabackend_obtenerreglasimpuestos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && !data.Error) setReglasImpuestos(data.reglas || []);
      })
      .catch(() => {
        if (!cancelled) setReglasImpuestos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedCompany, usuarioLog, claveLog, servidor]);

  const totalFactura = facturaCompleta
    ? parseFloat(facturaCompleta.total) || 0
    : facturaOrigen
    ? parseFloat(facturaOrigen.total) || 0
    : 0;
  const totalNotasCredito = facturaCompleta ? parseFloat(facturaCompleta.total_notas_credito) || 0 : 0;
  const montoDisponible = facturaCompleta
    ? parseFloat(facturaCompleta.monto_disponible_a_devolver) ?? totalFactura
    : totalFactura;
  const ncfFacturaOrigen =
    (facturaCompleta && facturaCompleta.ncf) || facturaOrigen?.ncf || '';

  /** Si la factura de origen no tuvo ITBIS, la nota de crédito tampoco debe calcularlo. */
  const facturaOrigenTieneImpuestos = useMemo(() => {
    const totalImp = parseFloat(facturaCompleta?.totalImpuestos) || 0;
    const itbisLista = parseFloat(facturaOrigen?.itbis) || 0;
    const montoImpuestos = (facturaCompleta?.impuestos || []).reduce(
      (s, imp) => s + (parseFloat(imp.monto) || 0),
      0
    );
    const itemsImp = (facturaCompleta?.items || []).reduce(
      (s, it) => s + (parseFloat(it.impuesto) || 0),
      0
    );
    return totalImp > 0.0001 || itbisLista > 0.0001 || montoImpuestos > 0.0001 || itemsImp > 0.0001;
  }, [facturaCompleta, facturaOrigen]);

  const cambiarCantidadDevolver = (index, value) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return;
    setItemsConCantidad((prev) => {
      const next = [...prev];
      const max = parseFloat(next[index].cantidad) || 0;
      next[index] = { ...next[index], cantidadDevolver: Math.min(num, max) };
      return next;
    });
  };

  const itemsADevolver = useMemo(
    () =>
      itemsConCantidad
        .filter((it) => (parseFloat(it.cantidadDevolver) || 0) > 0)
        .map((it) => {
          const cant = parseFloat(it.cantidadDevolver) || 0;
          const prec = parseFloat(it.precio) || 0;
          const desc = parseFloat(it.descuento) || 0;
          const subt = cant * prec - desc;
          return {
            codigo: it.codigo,
            descripcion: it.descripcion,
            cantidad: cant,
            precio: prec,
            descuento: desc,
            impuesto: facturaOrigenTieneImpuestos ? parseFloat(it.impuesto) || 0 : 0,
            subtotal: subt,
          };
        }),
    [itemsConCantidad, facturaOrigenTieneImpuestos]
  );

  const totalPorItems = useMemo(
    () => itemsADevolver.reduce((s, it) => s + (parseFloat(it.subtotal) || 0), 0),
    [itemsADevolver]
  );

  /** Nombre de impuesto que deben llevar las líneas para que `calcularimpuestos` las empareje con `reglas_impuestos.nombre`. */
  const nombreImpuestoParaReglas = useMemo(() => {
    if (!facturaOrigenTieneImpuestos) return '';
    const activas = (reglasImpuestos || [])
      .filter((r) => r.activa === 'S')
      .sort((a, b) => (parseFloat(a.orden) || 0) - (parseFloat(b.orden) || 0));
    if (activas.length > 0) return activas[0].nombre;
    return facturaCompleta?.impuestos?.[0]?.nombre || 'ITBIS';
  }, [reglasImpuestos, facturaCompleta?.impuestos, facturaOrigenTieneImpuestos]);

  /** Tasa (%) del impuesto aplicable, usada como `tasaitbis` en el payload del e-CF. */
  const tasaImpuestoNc = useMemo(() => {
    if (!facturaOrigenTieneImpuestos) return 0;
    const imp = (totalesNotaCredito.impuestos || [])[0];
    if (imp && parseFloat(imp.porcentaje) > 0) return parseFloat(imp.porcentaje);
    const activa = (reglasImpuestos || []).find(
      (r) => r.activa === 'S' && parseFloat(r.porcentaje) > 0
    );
    return activa ? parseFloat(activa.porcentaje) : 0;
  }, [totalesNotaCredito.impuestos, reglasImpuestos, facturaOrigenTieneImpuestos]);

  const subtotalNc = useMemo(() => {
    if (tipoDevolucion === 'monto') return Math.max(0, parseFloat(monto) || 0);
    return totalPorItems;
  }, [tipoDevolucion, monto, totalPorItems]);

  useEffect(() => {
    if (!open || !selectedCompany?.db_empresa || cargandoFactura) {
      setTotalesNotaCredito({ impuestos: [], totalImpuestos: 0, totalDescuentos: 0, total: 0 });
      setFiscalListo(!open || subtotalNc <= 0);
      return;
    }
    if (subtotalNc <= 0) {
      setTotalesNotaCredito({ impuestos: [], totalImpuestos: 0, totalDescuentos: 0, total: 0 });
      setFiscalListo(true);
      return;
    }
    if (!facturaOrigenTieneImpuestos) {
      setTotalesNotaCredito({ impuestos: [], totalImpuestos: 0, totalDescuentos: 0, total: subtotalNc });
      setFiscalListo(true);
      setCalculandoImpuestosNc(false);
      return;
    }

    setFiscalListo(false);
    let itemsfactura;
    if (tipoDevolucion === 'monto') {
      const m = subtotalNc;
      itemsfactura = [
        {
          codigo: 'MONTO',
          descripcion: 'Nota de crédito por monto',
          cantidad: 1,
          precio: m,
          descuento: 0,
          subtotal: m,
          descuento_general: 0,
          nombreimpuesto: nombreImpuestoParaReglas,
          tipoproducto: 'item',
        },
      ];
    } else {
      if (itemsADevolver.length === 0) {
        setTotalesNotaCredito({ impuestos: [], totalImpuestos: 0, totalDescuentos: 0, total: 0 });
        setFiscalListo(true);
        return;
      }
      itemsfactura = itemsADevolver.map((row) => ({
        codigo: row.codigo,
        descripcion: row.descripcion,
        cantidad: row.cantidad,
        precio: row.precio,
        descuento: parseFloat(row.descuento) || 0,
        subtotal: parseFloat(row.subtotal) || 0,
        descuento_general: 0,
        nombreimpuesto: nombreImpuestoParaReglas,
        tipoproducto: 'item',
      }));
    }

    let cancelled = false;
    setCalculandoImpuestosNc(true);
    fetch(servidor + '/api/facturabackend_calcularimpuestos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        subtotal: subtotalNc,
        itemsfactura,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || data.Error) {
          if (!cancelled && data.Error) {
            setTotalesNotaCredito({ impuestos: [], totalImpuestos: 0, totalDescuentos: 0, total: subtotalNc });
            setFiscalListo(true);
          }
          return;
        }
        const tImp = parseFloat(data.total_impuestos) || 0;
        const tDesc = parseFloat(data.total_descuentos) || 0;
        const total =
          data.total != null && !Number.isNaN(parseFloat(data.total))
            ? parseFloat(data.total)
            : subtotalNc + tImp - tDesc;
        const impArr = Array.isArray(data.impuestos)
          ? data.impuestos.map((imp) => ({
              id: imp.id || imp.codigo || imp.nombre,
              nombre: imp.nombre || imp.descripcion || 'Impuesto',
              porcentaje: parseFloat(imp.porcentaje) || 0,
              monto: parseFloat(imp.monto) || 0,
            }))
          : [];
        setTotalesNotaCredito({
          impuestos: impArr,
          totalImpuestos: tImp,
          totalDescuentos: tDesc,
          total: Math.round(total * 100) / 100,
        });
        setFiscalListo(true);
      })
      .catch(() => {
        if (!cancelled) {
          setTotalesNotaCredito({ impuestos: [], totalImpuestos: 0, totalDescuentos: 0, total: subtotalNc });
          setFiscalListo(true);
        }
      })
      .finally(() => {
        if (!cancelled) setCalculandoImpuestosNc(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    selectedCompany,
    usuarioLog,
    claveLog,
    servidor,
    cargandoFactura,
    tipoDevolucion,
    subtotalNc,
    itemsADevolver,
    nombreImpuestoParaReglas,
    facturaOrigenTieneImpuestos,
  ]);

  const totalNotaConFiscal = Math.max(0, totalesNotaCredito.total || subtotalNc);
  const deltaFiscal = Math.round((totalNotaConFiscal - subtotalNc) * 100) / 100;

  /** Reparte el ajuste fiscal (impuestos − descuentos según reglas) sobre `valorpagado` de cada línea; `impuesto` queda como monto de impuesto asignado a la línea. */
  const itemsADevolverConImpuesto = useMemo(() => {
    if (
      tipoDevolucion !== 'items' ||
      itemsADevolver.length === 0 ||
      subtotalNc <= 0 ||
      Math.abs(deltaFiscal) < 0.0005
    ) {
      return itemsADevolver;
    }
    let acc = 0;
    const n = itemsADevolver.length;
    return itemsADevolver.map((it, i) => {
      const isLast = i === n - 1;
      const share = isLast
        ? Math.round((deltaFiscal - acc) * 100) / 100
        : Math.round(((parseFloat(it.subtotal) || 0) / subtotalNc) * deltaFiscal * 100) / 100;
      if (!isLast) acc += share;
      const baseSub = parseFloat(it.subtotal) || 0;
      return {
        ...it,
        subtotal: Math.round((baseSub + share) * 100) / 100,
        impuesto: Math.round(share * 100) / 100,
      };
    });
  }, [tipoDevolucion, itemsADevolver, subtotalNc, deltaFiscal]);

  // Descarga en el navegador el Excel del e-CF devuelto por el endpoint de la DGII.
  const descargarArchivoECF = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'notacredito_ecf.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleGuardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para emitir notas de crédito.');
      return;
    }
    const estadoEcf = String(facturaOrigen?.ecf_estado || '').toLowerCase().trim();
    if (estadoEcf === 'rechazado' || estadoEcf === 'error') {
      showError(
        estadoEcf === 'rechazado'
          ? 'No se puede emitir nota de crédito: el e-CF fue rechazado por la DGII.'
          : 'No se puede emitir nota de crédito: el e-CF tiene error.'
      );
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!(motivo || '').trim()) {
      showError('Indique el motivo de la nota de crédito');
      return;
    }
    if (tipoDevolucion === 'monto') {
      const montoNum = parseFloat(monto) || 0;
      if (montoNum <= 0) {
        showError('El monto debe ser mayor que cero');
        return;
      }
      if (totalNotaConFiscal > montoDisponible + 0.005) {
        showError(
          `El total de la nota con impuestos ($${totalNotaConFiscal.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) no puede superar el disponible a devolver ($${montoDisponible.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
        );
        return;
      }
    } else {
      if (itemsADevolver.length === 0) {
        showError('Seleccione al menos un ítem con cantidad a devolver');
        return;
      }
      if (totalPorItems > montoDisponible + 0.005) {
        showError(
          `El subtotal por ítems ($${totalPorItems.toFixed(2)}) no puede superar el disponible a devolver ($${montoDisponible.toFixed(2)})`
        );
        return;
      }
      if (totalNotaConFiscal > montoDisponible + 0.005) {
        showError(
          `El total de la nota con impuestos ($${totalNotaConFiscal.toFixed(2)}) no puede superar el disponible a devolver ($${montoDisponible.toFixed(2)})`
        );
        return;
      }
    }

    setGuardando(true);
    try {
      const body = {
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        nfact_origen: facturaOrigen.nfact,
        tipofactura_origen: facturaOrigen.tipofactura,
        ncf_factura_origen: ncfFacturaOrigen,
        fechancfmodificado: facturaOrigen.fecha,
        motivo: motivo.trim(),
        montodisponible: montoDisponible,
        monto: monto,
        itbis: totalesNotaCredito.totalImpuestos || 0,
      };
      if (tipoDevolucion === 'monto') {
        body.monto = parseFloat(monto) || 0;
      } else {
        body.items = itemsADevolverConImpuesto;
      }

      const response = await fetch(servidor + '/api/facturabackend_guardarnotacredito', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok && !data.Error) {
        showSuccess(data.mensaje || 'Nota de crédito registrada correctamente');
        const totalNC = totalNotaConFiscal;
        const ncfNota = (data.ncf || '').trim();
        const itemsPrint =
          tipoDevolucion === 'monto'
            ? [
                {
                  codigo: 'MONTO',
                  descripcion: 'Devolución por monto',
                  cantidad: 1,
                  precio: subtotalNc,
                  descuento: 0,
                  subtotal: subtotalNc,
                  impuesto: totalesNotaCredito.totalImpuestos || 0,
                },
              ]
            : itemsADevolver.map((it) => ({ ...it, impuesto: it.impuesto || 0 }));
        const observacionesFull = [motivo.trim(), ncfFacturaOrigen ? `Factura origen NCF: ${ncfFacturaOrigen}` : '']
          .filter(Boolean)
          .join(' | ');

        // Comprobante fiscal electrónico (e-CF): si la empresa emite comprobante
        // electrónico y la nota lleva un NCF electrónico (E34), se envía el detalle
        // al endpoint de la DGII para generar el Excel del e-CF (referenciando el
        // NCF de la factura de origen como comprobante modificado). El backend
        // devuelve la fecha de validez del NCF y la URL del QR.
        // En histresu del POS, `suplidor` es el código del cliente (que suele ser
        // el RNC/cédula); el nombre real viene de maestcta vía `nombre_cliente`.
        const docCliente = (facturaCompleta?.rnc || facturaOrigen?.suplidor || '').trim();
        const docLimpio = docCliente.replace(/\D/g, '');
        const nombreCliente = (facturaCompleta?.nombre_cliente || facturaOrigen?.nombre_cliente || '').trim();
        const esElectronica = comprobanteelectronico === 'S' && ncfNota.slice(0, 1).toUpperCase() === 'E';
        const codigocomprobante =
          ncfNota.slice(0, 1).toUpperCase() === 'E' && /^\d{2}$/.test(ncfNota.slice(1, 3))
            ? ncfNota.slice(1, 3)
            : '';
        const codigoComprobanteOrigen =
          ncfFacturaOrigen.slice(0, 1).toUpperCase() === 'E' && /^\d{2}$/.test(ncfFacturaOrigen.slice(1, 3))
            ? ncfFacturaOrigen.slice(1, 3)
            : '';
        const origenEsConsumo = codigoComprobanteOrigen === '32';
        const rncComprador = origenEsConsumo
          ? (docLimpio && docLimpio !== '999999' ? docLimpio : '')
          : (docLimpio.length === 9 || docLimpio.length === 11 ? docLimpio : '');
        let fechaValidaNc = '';
        let qrUrlNc = '';
        let fechaFirmaNc = '';
        let codigoSeguridadNc = '';
        let montoTotalEcf = null;
        let fechaEmisionNc = '';
        if (esElectronica) {
          if (!ncfFacturaOrigen) {
            showError('La factura de origen no tiene NCF asignado. No se puede emitir el e-CF de la nota de crédito.');
            setGuardando(false);
            return;
          }
          if (!origenEsConsumo && !rncComprador) {
            showError('El cliente no tiene un RNC o cédula válido (9 o 11 dígitos). Se requiere para emitir nota de crédito electrónica de crédito fiscal.');
            setGuardando(false);
            return;
          }
          const itemsEcf =
            tipoDevolucion === 'monto'
              ? [
                  {
                    item: 'Nota de crédito por monto',
                    cantidad: 1,
                    valor: subtotalNc,
                    descuento: 0,
                    tasaitbis: (totalesNotaCredito.totalImpuestos || 0) > 0 ? tasaImpuestoNc : 0,
                  },
                ]
              : itemsADevolver.map((it) => ({
                  item: it.descripcion,
                  cantidad: parseFloat(it.cantidad) || 0,
                  valor: parseFloat(it.precio) || 0,
                  descuento: parseFloat(it.descuento) || 0,
                  tasaitbis: (parseFloat(it.impuesto) || 0) > 0 ? tasaImpuestoNc : 0,
                }));
          const ecfPayload = {
            comprobanteelectronico,
            rnc: rncComprador,
            razonsocial: nombreCliente,
            comprobante: ncfNota,
            tipocomprobante: 'Nota de Credito Electronica:E34',
            codigocomprobante,
            usuario: usuarioLog || '',
            password: claveLog || '',
            ncfmodificado: ncfFacturaOrigen,
            // FechaNCFModificado (obligatoria en el XML de la nota): fecha de la
            // factura de origen en formato dd-mm-aaaa.
            fechancfmodificado: (() => {
              const ff = (facturaCompleta?.fecha_firma || '').trim();
              if (ff) {
                const mFirma = ff.match(/^(\d{2}-\d{2}-\d{4})/);
                if (mFirma) return mFirma[1];
              }
              const f = String(facturaCompleta?.fecha || '').slice(0, 10);
              const m = f.match(/^(\d{4})-(\d{2})-(\d{2})/);
              return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
            })(),
            // Código de modificación DGII: 1 si la nota anula el total de la
            // factura, 3 si es corrección parcial. Verificado contra certecf: los
            // consumos E32 enviados (RFCE o completos) usan estos códigos normales;
            // el 5 aplica solo a facturas de consumo nunca remitidas a la DGII.
            codigomodificacion: totalNC >= totalFactura ? '1' : '3',
            razonmodificacion: motivo.trim().slice(0, 90),
            items: itemsEcf,
          };
          const ecf = await enviarPayloadFacturaElectronica(servidor, ecfPayload);
          if (ecf.ok) {
            descargarArchivoECF(ecf.blob, ecf.filename);
            fechaValidaNc = ecf.fechaValida || '';
          } else {
            showError(ecf.error || 'No se pudo generar el comprobante electrónico');
          }

          // Emisión real del e-CF a la DGII (firma + registro + envío). El QR,
          // la fecha de firma y el código de seguridad salen de aquí (igual
          // que FacturaPOS), no del Excel preliminar.
          const emision = await emitirEcfDgii(servidor, {
            ...ecfPayload,
            rncemisor: rncEmpresa || '',
            razonsocialemisor: typeof nombreNegocio === 'string' ? nombreNegocio : '',
            direccionemisor: direccionEmpresa || '',
          });
          if (emision.ok) {
            descargarArchivoECF(
              new Blob([emision.xml], { type: 'text/xml' }),
              `${emision.encf || 'ecf'}.xml`
            );
            fechaFirmaNc = emision.fecha_firma || '';
            codigoSeguridadNc = emision.codigo_seguridad || '';
            qrUrlNc = emision.qrUrl || '';
            montoTotalEcf = emision.monto_total ?? null;
            fechaEmisionNc = emision.fecha || '';
            const etiquetas = {
              aceptado: 'aceptada por la DGII',
              aceptado_condicional: 'aceptada condicional por la DGII',
              en_proceso: `en proceso en la DGII${emision.track_id ? ` (TrackId ${emision.track_id})` : ''}`,
              rechazado: 'RECHAZADA por la DGII',
              pendiente_envio: 'firmada; DGII inaccesible, se reenviará',
              error: 'firmada; el envío a la DGII falló',
            };
            const detalleDgii = (emision.respuesta_dgii?.mensajes || [])
              .map((m) => `${m.valor || ''}${m.codigo ? ` (${m.codigo})` : ''}`)
              .filter((t) => t.trim())
              .join(' · ');
            const mensajeDgii =
              `e-CF ${emision.encf} ${etiquetas[emision.estado] || emision.estado}` +
              (detalleDgii ? ` — DGII: ${detalleDgii}` : '');
            const estadoEcfNc = String(emision.estado || '').toLowerCase().trim();
            if (estadoEcfNc === 'rechazado' || estadoEcfNc === 'error') {
              showError(mensajeDgii);
              const rev = await revertirNotaCreditoRechazada(servidor, {
                id_empresa: selectedCompany,
                usuario: usuarioLog || '',
                password: claveLog || '',
                ncred: data.ncred,
                ncf: ncfNota,
              });
              if (rev.ok) {
                showError(
                  `Nota de crédito ${data.ncred} revertida automáticamente (e-CF ${estadoEcfNc}). Los montos quedaron en 0.`
                );
              } else {
                showError(rev.error || 'No se pudo revertir la nota de crédito');
              }
              onClose();
              return;
            }
            showSuccess(mensajeDgii);
          } else {
            showError(emision.error || 'No se pudo emitir el e-CF de la nota');
            const rev = await revertirNotaCreditoRechazada(servidor, {
              id_empresa: selectedCompany,
              usuario: usuarioLog || '',
              password: claveLog || '',
              ncred: data.ncred,
              ncf: ncfNota,
            });
            if (rev.ok) {
              showError(
                `Nota de crédito ${data.ncred} revertida automáticamente (error al emitir el e-CF). Los montos quedaron en 0.`
              );
            } else {
              showError(rev.error || 'No se pudo revertir la nota de crédito');
            }
            onClose();
            return;
          }
        }

        setDatosVistaNc({
          items: itemsPrint,
          impuestos: totalesNotaCredito.impuestos || [],
          subtotal: subtotalNc,
          totalDescuentos: totalesNotaCredito.totalDescuentos || 0,
          totalImpuestos: totalesNotaCredito.totalImpuestos || 0,
          total: totalNC,
          documento: 'Nota de Credito',
          numero: String(data.ncred ?? ''),
          fecha: (() => {
            const m = String(fechaEmisionNc || '').match(/^(\d{2})-(\d{2})-(\d{4})/);
            return m ? `${m[3]}-${m[2]}-${m[1]}` : new Date().toISOString().split('T')[0];
          })(),
          ncf: ncfNota,
          tipoDocumento: 'Nota de Crédito',
          tipo_documento: 'Nota de Credito',
          observaciones: observacionesFull,
          empresa: {
            nombre: nombreNegocio,
            direccion: direccionEmpresa,
            rnc: rncEmpresa,
          },
          cliente_nombre: nombreCliente || (facturaOrigen?.suplidor || '').trim(),
          id_empresa: selectedCompany,
          ncf_factura_origen: ncfFacturaOrigen,
          nfact_origen: facturaOrigen.nfact,
          tipofactura_origen: facturaOrigen.tipofactura,
          // Datos del comprobante fiscal electrónico para el QR y la fecha de validez.
          comprobanteelectronico: esElectronica ? 'S' : 'N',
          codigocomprobante,
          fechavencimiento: fechaValidaNc,
          rnc: rncComprador,
          qrUrl: qrUrlNc,
          fecha_firma: fechaFirmaNc,
          codigo_seguridad: codigoSeguridadNc,
          monto_total_ecf: montoTotalEcf,
        });
        setVistaNcAbierta(true);
      } else {
        showError(data.Error || 'No se pudo guardar la nota de crédito.');
      }
    } catch {
      showError('Error de conexión al guardar la nota de crédito');
    } finally {
      setGuardando(false);
    }
  };

  const cerrarVistaNc = () => {
    setVistaNcAbierta(false);
    setDatosVistaNc(null);
    onClose();
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.72rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  };

  return (
    <>
      <Dialog
        open={open && !vistaNcAbierta}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            Emitir nota de crédito
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          {!facturaOrigen ? (
            <Alert
              severity="info"
              sx={{ borderRadius: 2, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.infoSoft }}
            >
              Seleccione una factura para emitir una nota de crédito.
            </Alert>
          ) : cargandoFactura ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  mb: 2.5,
                  p: 2,
                  bgcolor: FP.color.surfaceAlt,
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: FP.color.textSecondary,
                    mb: 0.5,
                  }}
                >
                  Factura de origen
                </Typography>
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 0.5 }}>
                  {facturaOrigen.tipofactura} · Nº {facturaOrigen.nfact}
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                  Cliente: {facturaOrigen.suplidor || '—'}
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                  Total factura: $
                  {totalFactura.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                {totalNotasCredito > 0 && (
                  <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
                    Ya devuelto (N/C): $
                    {totalNotasCredito.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                )}
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary }}>
                  Monto disponible a devolver: $
                  {montoDisponible.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                {ncfFacturaOrigen && (
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                    NCF factura: <strong>{ncfFacturaOrigen}</strong>
                  </Typography>
                )}
              </Box>

              {montoDisponible <= 0 && (
                <Alert
                  severity="warning"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${FP.color.border}`,
                    bgcolor: FP.color.warningSoft,
                  }}
                >
                  No hay monto disponible a devolver: el total de esta factura ya fue cubierto por notas de crédito anteriores.
                </Alert>
              )}

              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border: `1px solid ${FP.color.border}`,
                  bgcolor: FP.color.infoSoft,
                }}
                icon={false}
              >
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.9rem' }}>
                  {facturaOrigenTieneImpuestos
                    ? 'Impuestos (reglas del sistema)'
                    : 'Impuestos'}
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                  {facturaOrigenTieneImpuestos ? (
                    <>
                      {reglasImpuestos.filter((r) => r.activa === 'S').length} regla(s) activa(s)
                      {nombreImpuestoParaReglas ? ` · Imputación: «${nombreImpuestoParaReglas}»` : ''}
                      {calculandoImpuestosNc && subtotalNc > 0 ? ' · Calculando…' : ''}
                    </>
                  ) : (
                    'La factura de origen no tiene ITBIS. Esta nota de crédito no calculará impuestos.'
                  )}
                </Typography>
              </Alert>

              {subtotalNc > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${FP.color.border}`,
                    bgcolor: FP.color.surfaceAlt,
                  }}
                >
                  <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.9rem' }}>
                    Subtotal nota: <strong>${subtotalNc.toFixed(2)}</strong>
                  </Typography>
                  {(totalesNotaCredito.impuestos || []).map((imp) => (
                    <Typography
                      key={String(imp.id ?? imp.nombre)}
                      sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}
                    >
                      {imp.nombre} ({imp.porcentaje}%): ${(parseFloat(imp.monto) || 0).toFixed(2)}
                    </Typography>
                  ))}
                  {(totalesNotaCredito.totalDescuentos || 0) > 0 && (
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                      Ajustes fiscales (restar): −${(totalesNotaCredito.totalDescuentos || 0).toFixed(2)}
                    </Typography>
                  )}
                  <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, mt: 0.5 }}>
                    Total nota de crédito: ${totalNotaConFiscal.toFixed(2)}
                  </Typography>
                  <Typography sx={{ color: FP.color.textMuted, fontSize: '0.78rem' }}>
                    {facturaOrigenTieneImpuestos
                      ? 'Debe ser menor o igual al monto disponible a devolver (incluye impuestos según reglas).'
                      : 'Debe ser menor o igual al monto disponible a devolver (sin impuestos: la factura de origen no tiene ITBIS).'}
                  </Typography>
                </Paper>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Motivo"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    multiline
                    rows={2}
                    placeholder="Ej: Devolución, descuento posterior..."
                    disabled={!puedeModificar || !usuarioLog}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel
                      component="legend"
                      sx={{
                        color: FP.color.textSecondary,
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Tipo de devolución
                    </FormLabel>
                    <RadioGroup
                      row
                      value={tipoDevolucion}
                      onChange={(e) => setTipoDevolucion(e.target.value)}
                    >
                      <FormControlLabel
                        value="monto"
                        control={<Radio sx={{ '&.Mui-checked': { color: FP.color.accentBlue } }} />}
                        label="Monto específico"
                      />
                      <FormControlLabel
                        value="items"
                        control={<Radio sx={{ '&.Mui-checked': { color: FP.color.accentBlue } }} />}
                        label="Devolver por ítems"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {tipoDevolucion === 'monto' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Monto (nota de crédito)"
                      type="number"
                      inputProps={{ min: 0, max: montoDisponible, step: 0.01 }}
                      value={monto}
                      onChange={(e) => setMonto(e.target.value)}
                      helperText={`Máximo disponible: $${montoDisponible.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      disabled={!puedeModificar || !usuarioLog}
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  </Grid>
                )}
                {tipoDevolucion === 'items' && facturaCompleta?.items?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        color: FP.color.textSecondary,
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        mb: 1,
                      }}
                    >
                      Cantidad a devolver por ítem
                    </Typography>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        maxHeight: 280,
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableBody>
                          <TableRow
                            sx={{
                              '& td': {
                                border: 'none',
                                borderBottom: `2px solid ${FP.color.border}`,
                                bgcolor: FP.color.surfaceAlt,
                              },
                            }}
                          >
                            <TableCell sx={cellHeadSx}>Código</TableCell>
                            <TableCell sx={cellHeadSx}>Descripción</TableCell>
                            <TableCell align="right" sx={cellHeadSx}>
                              Cant. facturada
                            </TableCell>
                            <TableCell align="right" sx={cellHeadSx}>
                              Precio
                            </TableCell>
                            <TableCell align="right" sx={cellHeadSx}>
                              Cant. a devolver
                            </TableCell>
                            <TableCell align="right" sx={cellHeadSx}>
                              Subtotal
                            </TableCell>
                          </TableRow>
                          {itemsConCantidad.map((it, idx) => {
                            const cantDev = parseFloat(it.cantidadDevolver) || 0;
                            const subt = cantDev * (parseFloat(it.precio) || 0) - (parseFloat(it.descuento) || 0);
                            return (
                              <TableRow key={idx} hover>
                                <TableCell>{it.codigo}</TableCell>
                                <TableCell>{it.descripcion}</TableCell>
                                <TableCell align="right">{parseFloat(it.cantidad) || 0}</TableCell>
                                <TableCell align="right">${(parseFloat(it.precio) || 0).toFixed(2)}</TableCell>
                                <TableCell align="right">
                                  <TextField
                                    type="number"
                                    size="small"
                                    inputProps={{ min: 0, max: it.cantidad, step: 0.01 }}
                                    value={it.cantidadDevolver}
                                    onChange={(e) => cambiarCantidadDevolver(idx, e.target.value)}
                                    disabled={!puedeModificar || !usuarioLog}
                                    sx={{ width: 100, ...fpInputSx }}
                                  />
                                </TableCell>
                                <TableCell align="right">${subt.toFixed(2)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                      <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.9rem' }}>
                        Subtotal ítems: <strong>${totalPorItems.toFixed(2)}</strong>
                      </Typography>
                      <Typography sx={{ color: FP.color.textMuted, fontSize: '0.82rem' }}>
                        Monto disponible:{' '}
                        <strong>
                          ${montoDisponible.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </strong>{' '}
                        {facturaOrigenTieneImpuestos
                          ? ' · Total con impuestos arriba'
                          : ' · Sin impuestos (la factura de origen no tiene ITBIS)'}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={onClose}
            startIcon={<CloseIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cerrar
          </Button>
          {facturaOrigen && !cargandoFactura && (
            <Button
              variant="contained"
              onClick={handleGuardar}
              disabled={
                guardando ||
                montoDisponible <= 0 ||
                !fiscalListo ||
                (subtotalNc > 0 && calculandoImpuestosNc) ||
                !puedeModificar ||
                !usuarioLog
              }
              startIcon={guardando ? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} /> : <SaveIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: 1.4,
                fontWeight: 700,
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              {guardando ? 'Guardando…' : 'Guardar nota de crédito'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {datosVistaNc && (
        <VistaNotaCredito open={vistaNcAbierta} onClose={cerrarVistaNc} notaCreditoData={datosVistaNc} />
      )}
    </>
  );
};

export default NotaCredito;
